import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { formatCents } from '../../utils/subscriptionFormat';

export const formatDateTime = (value) => {
  try {
    return format(new Date(value), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR });
  } catch (e) {
    return value;
  }
};

// Só o dia (próxima cobrança, fim do período de teste)
const formatDay = (value) => {
  try {
    return format(new Date(value), 'dd/MM/yyyy', { locale: ptBR });
  } catch (e) {
    return value;
  }
};

/** Selo de direção da troca de plano. Sem a direção gravada, não mostra selo. */
export const getChangeType = (action, details) => {
  if (action !== 'plan_changed' || !details) return null;
  if (details.is_upgrade) return { tone: 'success', text: 'Upgrade' };
  if (details.is_downgrade) return { tone: 'warning', text: 'Downgrade' };
  return null;
};

// Frase da situação gravada numa atualização da assinatura
const getSubscriptionUpdateDescription = (details) => {
  const cancelAtPeriodEnd = details?.cancel_at_period_end === true || details?.cancel_at_period_end === 'true';
  if (cancelAtPeriodEnd || details?.status === 'canceling') {
    return 'Cancelamento agendado para o fim do período';
  }
  switch (details?.status) {
    case 'active': return 'Assinatura ativa';
    case 'trialing': return 'Período de teste';
    case 'past_due':
    case 'unpaid': return 'Pagamento em atraso';
    case 'canceled': return 'Assinatura encerrada';
    default: return 'Assinatura atualizada';
  }
};

/** Descrição da ação, numa frase */
export const getActionDescription = (action, details) => {
  switch (action) {
    case 'plan_changed': return 'Troca de plano';
    case 'subscription_created': return 'Assinatura iniciada';
    case 'subscription_updated': return getSubscriptionUpdateDescription(details);
    case 'subscription_canceling': return 'Cancelamento agendado para o fim do período';
    case 'subscription_canceled': return 'Cancelamento solicitado';
    case 'subscription_deleted': return 'Assinatura encerrada';
    case 'addon_subscription_canceled': return 'Adicional cancelado';
    case 'addons_canceled':
    case 'addons_canceled_automatically': return 'Adicionais cancelados junto com o plano';
    case 'payment_received':
    case 'payment_processed': return 'Pagamento recebido';
    case 'payment_failed': return 'Pagamento recusado';
    case 'payment_pending': return 'Pagamento pendente';
    default: return 'Atualização da assinatura';
  }
};

const planChangeLines = (details, planName) => {
  if (!details.new_plan_id) return [];
  const lines = [];
  const { old_plan_id: oldId, new_plan_id: newId } = details;
  // Registros antigos gravaram o plano novo nos dois campos: mostra só o plano
  if (oldId && String(oldId) !== String(newId)) {
    lines.push(`De ${planName(oldId)} para ${planName(newId)}`);
  } else {
    lines.push(`Plano ${planName(newId)}`);
  }
  if (details.proration_amount_cents > 0) lines.push(`Valor proporcional: ${formatCents(details.proration_amount_cents)}`);
  if (details.effective_date) lines.push(`Data efetiva: ${formatDateTime(details.effective_date)}`);
  return lines;
};

const paymentLines = (details, planName) => {
  const lines = [];
  if (details.amount_cents > 0) lines.push(`Valor: ${formatCents(details.amount_cents)}`);
  if (details.plan_id) lines.push(`Plano ${planName(details.plan_id)}`);
  if (details.paid_date) lines.push(`Pago em ${formatDateTime(details.paid_date)}`);
  if (details.due_date) lines.push(`Vencimento: ${formatDateTime(details.due_date)}`);
  return lines;
};

const subscriptionLines = (details, planName) => {
  const lines = [];
  if (details.plan_id) lines.push(`Plano ${planName(details.plan_id)}`);
  if (details.next_due_date && details.status !== 'canceled') lines.push(`Próxima cobrança: ${formatDay(details.next_due_date)}`);
  if (details.trial_ends_at) lines.push(`Fim do período de teste: ${formatDay(details.trial_ends_at)}`);
  if (details.canceled_at) lines.push(`Encerrada em ${formatDateTime(details.canceled_at)}`);
  return lines;
};

/** Linhas de detalhe de cada registro, conforme a ação */
export const getDetailLines = (action, details, plans) => {
  if (!details) return [];
  const planName = (planId) => plans[planId]?.name || `#${planId}`;
  switch (action) {
    case 'plan_changed':
      return planChangeLines(details, planName);
    case 'payment_received':
    case 'payment_processed':
    case 'payment_failed':
    case 'payment_pending':
      return paymentLines(details, planName);
    case 'subscription_created':
    case 'subscription_updated':
    case 'subscription_canceled':
    case 'subscription_deleted':
      return subscriptionLines(details, planName);
    default:
      return [];
  }
};
